import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from '../nav';
import { CustomIcon } from '../components/CustomIcons';
import { findService } from '../lib/bills';
import { BALANCE, formatAmount } from '../lib/transfer';
type Step = 'biller' | 'amount';

/**
 * Bill and airtime entry. A payment is only ever prepared here: the customer
 * picks the biller, enters the reference, chooses the amount or package, and
 * every value is carried into the reviewable draft unchanged.
 */
export function BillPay() {
  const navigate = useNavigate();
  const {
    search
  } = useLocation();
  const params = new URLSearchParams(search);
  const service = findService(params.get('service'));
  const [step, setStep] = useState<Step>('biller');
  const [provider, setProvider] = useState(params.get('prov') || '');
  const [target, setTarget] = useState(params.get('target') || '');
  const [bundle, setBundle] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [saveBiller, setSaveBiller] = useState(false);
  const digits = target.replace(/\D/g, '');
  const referenceReady = digits.length >= service.inputDigits;
  const billerReady = Boolean(provider) && referenceReady;
  const selectedBundle = service.bundles?.find(item => item.label === bundle);
  const amountValue = useMemo(() => {
    if (service.mode === 'bundle') return selectedBundle?.amount || 0;
    return Number(amount.replace(/[^\d.]/g, '')) || 0;
  }, [amount, selectedBundle, service.mode]);
  const overBalance = amountValue > BALANCE;
  const amountReady = amountValue > 0 && !overBalance;
  const submit = () => {
    if (!amountReady) return;
    const query = new URLSearchParams({
      bill: service.name,
      prov: provider,
      target,
      amt: String(amountValue),
      plan: selectedBundle ? selectedBundle.label : `${service.name} payment`
    });
    navigate(`/draft?${query.toString()}`);
  };
  return <main className="flex h-full w-full flex-col bg-brand-cloud" data-id="element-252" data-mp="2pkzofj6nzboq">
      <header className="shrink-0 bg-white px-5 pb-4 pt-14" data-id="element-253" data-mp="3f8btb4mq3if3">
        <div className="flex items-center gap-3" data-id="element-254" data-mp="3f7tymcfplc9a">
          <button onClick={() => step === 'amount' ? setStep('biller') : navigate(-1)} aria-label="Go back" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-cloud text-brand-navy" data-id="element-255" data-mp="3j9p0pj5grto4">
            ←
          </button>
          <div className="min-w-0 flex-1" data-id="element-256" data-mp="2w9r6horv6z79">
            <p className="text-[10px] font-extrabold uppercase tracking-[.16em] text-brand-cobalt" data-id="element-257" data-mp="2waspqgj1as02">
              Step {step === 'biller' ? '1' : '2'} of 2
            </p>
            <h1 className="mt-1 truncate text-lg font-extrabold tracking-tight text-brand-navy" data-id="element-258" data-mp="359wcbuhetpjb">
              {step === 'biller' ? `Pay ${service.name.toLowerCase()}` : service.mode === 'bundle' ? 'Choose a package' : 'Enter amount'}
            </h1>
          </div>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-cloud text-brand-cobalt" data-id="element-259" data-mp="pj1fwiarhqa7">
            <CustomIcon name={service.icon} size={20} data-id="element-260" data-mp="2a1lonxde3lh2" />
          </span>
        </div>
        <div className="mt-4 flex gap-1.5" aria-hidden="true" data-id="element-261" data-mp="25294jkdwksi6">
          <span className="h-1 flex-1 rounded-full bg-brand-cobalt" data-id="element-262" data-mp="252r68wjc3yxh" />
          <span className={`h-1 flex-1 rounded-full ${step === 'amount' ? 'bg-brand-cobalt' : 'bg-slate-200'}`} data-id="element-263" data-mp="20a7mfhu207k8" />
        </div>
      </header>

      <div className="phone-scroll min-h-0 flex-1 overflow-y-auto px-5 py-5" data-id="element-264" data-mp="1vabqm5vifmfr">
        {step === 'biller' ? <>
            <fieldset data-id="element-265" data-mp="1mayfz9vngrtg">
              <legend className="text-xs font-extrabold text-brand-navy" data-id="element-266" data-mp="1mag9lkf9wx7t">Select biller</legend>
              <div className="mt-3 grid grid-cols-2 gap-2.5" data-id="element-267" data-mp="1hdg3xamc5wh8">
                {service.providers.map(item => {
              const active = provider === item;
              return <button key={item} type="button" aria-pressed={active} onClick={() => setProvider(active ? '' : item)} className={`flex min-h-[52px] items-center justify-center rounded-2xl px-3 text-center text-xs font-extrabold transition-colors ${active ? 'bg-brand-cobalt text-white' : 'bg-white text-brand-navy shadow-soft'}`} data-id="element-268" data-mp="3bqo0p7tvgi5i">
                      {item}
                    </button>;
            })}
              </div>
            </fieldset>

            <div className="mt-6" data-id="element-269" data-mp="2w0hy4yold510">
              <label htmlFor="bill-reference" className="text-xs font-extrabold text-brand-navy" data-id="element-270" data-mp="2w083uywikbpi">
                {service.inputLabel}
              </label>
              <input id="bill-reference" value={target} onChange={event => setTarget(event.target.value)} inputMode={service.inputDigits > 6 ? 'numeric' : 'text'} placeholder={service.inputHint} className="mt-2 h-14 w-full rounded-2xl bg-white px-4 text-sm font-extrabold tabular-nums text-brand-navy shadow-soft outline-none placeholder:font-bold placeholder:text-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-cobalt" data-id="element-271" data-mp="2m44w9diw7kv6" />
              <p className="mt-2 text-[11px] font-semibold text-brand-slate" data-id="element-272" data-mp="27hpob3pc3mwl">
                {referenceReady ? 'Reference looks complete. Check it before you continue.' : `Enter at least ${service.inputDigits} digits.`}
              </p>
            </div>

            <aside className="mt-6 rounded-[22px] bg-white p-4 shadow-soft" data-id="element-273" data-mp="2hchomfaawnv2">
              <p className="text-[10px] font-extrabold uppercase tracking-[.14em] text-brand-slate" data-id="element-274" data-mp="2cfhgzzxszd1u">Before you pay</p>
              <p className="mt-1.5 text-xs leading-relaxed text-brand-slate" data-id="element-275" data-mp="2q8yxtcz3ubo6">
                Bill references cannot be reversed once a biller accepts them. VE Bank shows you the full amount and fee before your confirmation.
              </p>
            </aside>
          </> : <>
            <article className="flex items-center gap-3 rounded-[22px] bg-white p-4 shadow-soft" data-id="element-276" data-mp="1sst62y3osngo">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-cloud text-brand-cobalt" data-id="element-277" data-mp="1wueea65a0dkj">
                <CustomIcon name={service.icon} size={20} data-id="element-278" data-mp="21t96tzt1pghu" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-279" data-mp="26jxypg8n0fhi">
                <p className="truncate text-sm font-extrabold text-brand-navy" data-id="element-280" data-mp="26lsk0vntm9m4">{provider}</p>
                <p className="mt-0.5 truncate text-[11px] font-semibold text-brand-slate" data-id="element-281" data-mp="2bij0hk3jf782">{target}</p>
              </div>
              <button onClick={() => setStep('biller')} className="shrink-0 text-[11px] font-extrabold text-brand-cobalt" data-id="element-282" data-mp="2ggkkc78sfakl">
                Change
              </button>
            </article>

            {service.mode === 'bundle' ? <fieldset className="mt-5" data-id="element-283" data-mp="q67tu79p1n6t">
                <legend className="text-xs font-extrabold text-brand-navy" data-id="element-284" data-mp="q79dh04rzi05">Available packages</legend>
                <div className="mt-3 space-y-2.5" data-id="element-285" data-mp="v5kq1imgg6m2">
                  {service.bundles?.map(item => {
              const active = bundle === item.label;
              return <button key={item.label} type="button" aria-pressed={active} onClick={() => setBundle(active ? '' : item.label)} className={`flex min-h-[64px] w-full items-center gap-3 rounded-[20px] px-4 text-left transition-colors ${active ? 'bg-brand-cobalt text-white' : 'bg-white text-brand-navy shadow-soft'}`} data-id="element-286" data-mp="iu1kvsq5kdmt">
                        <span className="min-w-0 flex-1" data-id="element-287" data-mp="wloq4i22owhq">
                          <span className="block text-sm font-extrabold" data-id="element-288" data-mp="woawwfh537yw">{item.label}</span>
                          <span className={`mt-0.5 block text-[11px] font-semibold ${active ? 'text-blue-100' : 'text-brand-slate'}`} data-id="element-289" data-mp="skcvtz897n37">{item.detail}</span>
                        </span>
                        <span className="shrink-0 text-sm font-extrabold tabular-nums" data-id="element-290" data-mp="3d9ah16yx7l89">₦{formatAmount(item.amount)}</span>
                      </button>;
            })}
                </div>
              </fieldset> : <div className="mt-5" data-id="element-291" data-mp="179t075natd9a">
                <label htmlFor="bill-amount" className="text-xs font-extrabold text-brand-navy" data-id="element-292" data-mp="17auinv6n9211">
                  Amount
                </label>
                <div className="mt-2 flex h-16 items-center gap-2 rounded-2xl bg-white px-4 shadow-soft" data-id="element-293" data-mp="1zy6vhdn05vh5">
                  <span className="text-lg font-extrabold text-brand-slate" data-id="element-294" data-mp="23zk4txpb0gst">₦</span>
                  <input id="bill-amount" value={amount} onChange={event => setAmount(event.target.value)} inputMode="decimal" placeholder="0.00" className="min-w-0 flex-1 bg-transparent text-2xl font-extrabold tabular-nums tracking-[-.03em] text-brand-navy outline-none placeholder:text-slate-300" data-id="element-295" data-mp="241muggmjo7x0" />
                </div>
                <div className="mt-3 flex gap-2" data-id="element-296" data-mp="2x11ow9oijtzi">
                  {service.quickAmounts?.map(value => <button key={value} type="button" onClick={() => setAmount(String(value))} className="min-h-[40px] flex-1 rounded-xl bg-white text-[11px] font-extrabold text-brand-cobalt shadow-soft" data-id="element-297" data-mp="2t0r8e333hl6e">
                      ₦{value.toLocaleString('en-NG')}
                    </button>)}
                </div>
                <p className={`mt-3 text-[11px] font-bold ${overBalance ? 'text-brand-orange' : 'text-brand-slate'}`} data-id="element-298" data-mp="2o1n15hzqdxip">
                  {overBalance ? 'This is more than your available balance.' : `Available balance ₦${formatAmount(BALANCE)}`}
                </p>
              </div>}

            <button type="button" role="switch" aria-checked={saveBiller} onClick={() => setSaveBiller(state => !state)} className="mt-5 flex min-h-[56px] w-full items-center justify-between rounded-[20px] bg-white px-4 text-left shadow-soft" data-id="element-299" data-mp="3ui8sffiwa2o7">
              <span className="text-xs font-extrabold text-brand-navy" data-id="element-300" data-mp="3vfbkr0wds0ba">Save this biller for next time</span>
              <span className={`flex h-7 w-12 shrink-0 rounded-full p-1 transition-colors ${saveBiller ? 'bg-brand-cobalt' : 'bg-slate-200'}`} data-id="element-301" data-mp="3qgih006wbnob">
                <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${saveBiller ? 'translate-x-5' : ''}`} data-id="element-302" data-mp="3mdc5ad10l4ag" />
              </span>
            </button>

            {amountReady && <dl className="mt-5 rounded-[22px] bg-white p-4 shadow-soft" data-id="element-303" data-mp="37ifo3ab896l8">
                <Row label="Amount" value={`₦${formatAmount(amountValue)}`} data-id="element-304" data-mp="37kingky9sc2m" />
                <Row label="Fee" value="₦0.00" data-id="element-305" data-mp="1apm6aozlagj4" />
                <Row label="Total debit" value={`₦${formatAmount(amountValue)}`} strong data-id="element-306" data-mp="15rawge9ao60w" />
              </dl>}
          </>}
      </div>

      <footer className="shrink-0 border-t border-slate-200 bg-white p-5" data-id="element-307" data-mp="1e7jxgxrjwwa0">
        {step === 'biller' ? <button onClick={() => setStep('amount')} disabled={!billerReady} className="flex min-h-[54px] w-full items-center justify-center rounded-2xl bg-brand-cobalt text-sm font-extrabold text-white transition-opacity disabled:bg-slate-200 disabled:text-slate-400" data-id="element-308" data-mp="1i7u6ykejy4tm">
            Continue
          </button> : <button onClick={submit} disabled={!amountReady} className="flex min-h-[54px] w-full items-center justify-between rounded-2xl bg-brand-cobalt px-5 text-sm font-extrabold text-white transition-opacity disabled:bg-slate-200 disabled:text-slate-400" data-id="element-309" data-mp="zbu8cqr2k312">
            Prepare payment <span aria-hidden="true" data-id="element-310" data-mp="2fosuvqlfeu3f">→</span>
          </button>}
      </footer>
    </main>;
}
function Row({
  label,
  value,
  strong = false
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-2.5 last:border-0 last:pb-0 first:pt-0" data-id="element-311" data-mp="1v230el5gtk81">
      <dt className="text-[11px] font-bold text-brand-slate" data-id="element-312" data-mp="24xwdc5n3jfqc">{label}</dt>
      <dd className={`text-xs tabular-nums ${strong ? 'font-extrabold text-brand-cobalt' : 'font-bold text-brand-navy'}`} data-id="element-313" data-mp="24vtnblly1mlm">{value}</dd>
    </div>;
}